import type { LaneCard } from 'app/pages/CommissionManager/types';
import type { AccommodationCondition, ProductTypeCondition } from 'app/pages/CommissionManager/types';
import { CommissionProductSelectMode } from 'app/shared/api/commissionGroup/types';
import type { TaxBandApiDto } from './types';
import type { TaxBandRule } from 'app/pages/TaxManager/taxTypes';

const fmt = (prefix: string, id: number) => `${prefix}-${id}`;

function commissionKind(value: number | string | undefined | null): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function userCards(cards: LaneCard[]): LaneCard[] {
  return cards.filter((c) => !c.isMirror);
}

type TaxFieldItem = Record<string, unknown>;

const nullWeeks = { minWeeks: null as number | null, maxWeeks: null as number | null };

function laneCardToTaxItem(card: LaneCard): TaxFieldItem | null {
  const cond = card.condition;

  switch (card.productType) {
    case 'Course': {
      if (!cond || cond.mode === 'ANY') {
        return {
          field: 'course',
          mode: CommissionProductSelectMode.Any,
          courseIds: [],
          ...nullWeeks,
        };
      }
      if (cond.mode === 'SELECT' && 'selectedIds' in cond && cond.selectedIds.length > 0) {
        return {
          field: 'course',
          mode: CommissionProductSelectMode.Select,
          courseIds: cond.selectedIds.map((id) => fmt('Course', id)),
          ...nullWeeks,
        };
      }
      return null;
    }
    case 'Transfer': {
      if (!cond || cond.mode === 'ANY') {
        return { field: 'transfer', mode: CommissionProductSelectMode.Any, transferIds: [] };
      }
      if (cond.mode === 'SELECT' && 'selectedIds' in cond && cond.selectedIds.length > 0) {
        return {
          field: 'transfer',
          mode: CommissionProductSelectMode.Select,
          transferIds: cond.selectedIds.map((id) => fmt('Transfer', id)),
        };
      }
      return null;
    }
    case 'Accommodation': {
      const c = cond as AccommodationCondition | undefined;
      if (!c || c.mode === 'ANY') {
        return {
          field: 'accommodation',
          mode: CommissionProductSelectMode.Any,
          kind: null,
          accommodationIds: [],
          ...nullWeeks,
        };
      }
      if (c.mode === 'CHOOSE_TYPE') {
        const kind = commissionKind(c.typeId);
        if (kind == null) return null;
        return {
          field: 'accommodation',
          mode: CommissionProductSelectMode.Type,
          kind,
          accommodationIds: [],
          ...nullWeeks,
        };
      }
      if (c.mode === 'SELECT' && c.selectedIds?.length) {
        return {
          field: 'accommodation',
          mode: CommissionProductSelectMode.Select,
          kind: null,
          accommodationIds: c.selectedIds.map((id) => fmt('Accommodation', id)),
          ...nullWeeks,
        };
      }
      return null;
    }
    case 'AddOn': {
      const c = cond as AccommodationCondition | undefined;
      if (!c || c.mode === 'ANY') {
        return { field: 'addOn', mode: CommissionProductSelectMode.Any, kind: null, addOnIds: [] };
      }
      if (c.mode === 'CHOOSE_TYPE') {
        const kind = commissionKind(c.typeId);
        if (kind == null) return null;
        return { field: 'addOn', mode: CommissionProductSelectMode.Type, kind, addOnIds: [] };
      }
      if (c.mode === 'SELECT' && c.selectedIds?.length) {
        return {
          field: 'addOn',
          mode: CommissionProductSelectMode.Select,
          kind: null,
          addOnIds: c.selectedIds.map((id) => fmt('AddOn', id)),
        };
      }
      return null;
    }
    case 'Fees': {
      if (!cond || cond.mode === 'ANY') {
        return { field: 'fee', mode: CommissionProductSelectMode.Any, feeIds: [] };
      }
      if (cond.mode === 'SELECT' && 'selectedIds' in cond && cond.selectedIds.length > 0) {
        return {
          field: 'fee',
          mode: CommissionProductSelectMode.Select,
          feeIds: cond.selectedIds.map((id) => fmt('Fee', id)),
        };
      }
      return null;
    }
    default:
      return null;
  }
}

function taxItemKindFromField(item: TaxFieldItem): 'course' | 'accommodation' | 'addOn' | 'transfer' | 'fee' | null {
  const f = item.field;
  if (typeof f === 'string') {
    const key = f.toLowerCase();
    if (key === 'course') return 'course';
    if (key === 'accommodation') return 'accommodation';
    if (key === 'addon') return 'addOn';
    if (key === 'transfer') return 'transfer';
    if (key === 'fee') return 'fee';
  }
  return null;
}

function taxItemKind(item: TaxFieldItem): 'course' | 'accommodation' | 'addOn' | 'transfer' | 'fee' | null {
  const fromDisc = taxItemKindFromField(item);
  if (fromDisc) return fromDisc;
  if (Object.prototype.hasOwnProperty.call(item, 'courseIds')) return 'course';
  if (Object.prototype.hasOwnProperty.call(item, 'accommodationIds')) return 'accommodation';
  if (Object.prototype.hasOwnProperty.call(item, 'addOnIds')) return 'addOn';
  if (Object.prototype.hasOwnProperty.call(item, 'transferIds')) return 'transfer';
  if (Object.prototype.hasOwnProperty.call(item, 'feeIds')) return 'fee';
  return null;
}

function taxFieldDiscriminator(item: TaxFieldItem): string | null {
  const f = item.field;
  if (typeof f === 'string' && f.trim() !== '') return f;
  const k = taxItemKind(item);
  if (!k) return null;
  return k === 'addOn' ? 'addOn' : k;
}

function includeAnyStubForTaxField(field: string): TaxFieldItem | null {
  switch (field) {
    case 'course':
      return {
        field: 'course',
        mode: CommissionProductSelectMode.Any,
        courseIds: [],
        ...nullWeeks,
      };
    case 'transfer':
      return { field: 'transfer', mode: CommissionProductSelectMode.Any, transferIds: [] };
    case 'accommodation':
      return {
        field: 'accommodation',
        mode: CommissionProductSelectMode.Any,
        kind: null,
        accommodationIds: [],
        ...nullWeeks,
      };
    case 'addOn':
      return { field: 'addOn', mode: CommissionProductSelectMode.Any, kind: null, addOnIds: [] };
    case 'fee':
      return { field: 'fee', mode: CommissionProductSelectMode.Any, feeIds: [] };
    default:
      return null;
  }
}

function ensureIncludeForEveryTaxExclude(include: TaxFieldItem[], exclude: TaxFieldItem[]): TaxFieldItem[] {
  const seen = new Set<string>();
  for (const inc of include) {
    const f = taxFieldDiscriminator(inc);
    if (f) seen.add(f);
  }
  const stubs: TaxFieldItem[] = [];
  for (const ex of exclude) {
    const f = taxFieldDiscriminator(ex);
    if (!f || seen.has(f)) continue;
    const stub = includeAnyStubForTaxField(f);
    if (!stub) continue;
    seen.add(f);
    stubs.push(stub);
  }
  return [...include, ...stubs];
}

export function taxBandRuleToApiBand(rule: TaxBandRule): TaxBandApiDto {
  let include = userCards(rule.include)
    .map(laneCardToTaxItem)
    .filter((x): x is TaxFieldItem => x != null);
  const exclude = userCards(rule.exclude)
    .map(laneCardToTaxItem)
    .filter((x): x is TaxFieldItem => x != null);

  include = ensureIncludeForEveryTaxExclude(include, exclude);

  const numericId = rule.id.startsWith('band-') ? 0 : Number(rule.id);
  return {
    id: Number.isFinite(numericId) ? numericId : 0,
    name: rule.name || '',
    taxCode: (rule.taxCode || '').replace(/\D/g, ''),
    taxRatePercent: Number(rule.taxRatePercent) || 0,
    include,
    exclude,
  };
}

function parsePrefixedIds(ids: unknown[], prefix: string): number[] {
  if (!Array.isArray(ids)) return [];
  const out: number[] = [];
  for (const raw of ids) {
    const s = String(raw);
    if (s.startsWith(`${prefix}-`)) {
      const n = Number(s.slice(prefix.length + 1));
      if (Number.isFinite(n)) out.push(n);
    } else {
      const n = Number(s);
      if (Number.isFinite(n)) out.push(n);
    }
  }
  return out;
}

export type TaxProductNameLookup = {
  courses: Map<number, string>;
  accommodations: Map<number, string>;
  addons: Map<number, string>;
  transfers: Map<number, string>;
  accommodationTypes: Map<number, string>;
  addOnTypes: Map<number, string>;
  fees: Map<number, string>;
};

function namesForIds(ids: number[], map: Map<number, string>): string[] {
  return ids.map((id) => map.get(id) ?? '').filter(Boolean);
}

export function taxApiItemToLaneCard(raw: TaxFieldItem, index: number): LaneCard | null {
  const mode = Number(raw.mode ?? 0);
  const kind = taxItemKind(raw);

  switch (kind) {
    case 'course': {
      const ids = parsePrefixedIds((raw.courseIds as unknown[]) ?? [], 'Course');
      if (mode === CommissionProductSelectMode.Any || ids.length === 0) {
        return {
          id: `tax-inc-${index}`,
          productType: 'Course',
          summary: 'Any',
          condition: { mode: 'ANY', selectedIds: [] },
        };
      }
      const cond: ProductTypeCondition = { mode: 'SELECT', selectedIds: ids };
      return {
        id: `tax-inc-${index}`,
        productType: 'Course',
        summary: 'Specify product',
        condition: cond,
      };
    }
    case 'transfer': {
      const ids = parsePrefixedIds((raw.transferIds as unknown[]) ?? [], 'Transfer');
      if (mode === CommissionProductSelectMode.Any || ids.length === 0) {
        return {
          id: `tax-inc-${index}`,
          productType: 'Transfer',
          summary: 'Any',
          condition: { mode: 'ANY', selectedIds: [] },
        };
      }
      return {
        id: `tax-inc-${index}`,
        productType: 'Transfer',
        summary: ids.length ? String(ids.length) : 'Specify product',
        condition: { mode: 'SELECT', selectedIds: ids },
      };
    }
    case 'accommodation': {
      const ids = parsePrefixedIds((raw.accommodationIds as unknown[]) ?? [], 'Accommodation');
      const kindNum = raw.kind != null && Number(raw.kind) > 0 ? Number(raw.kind) : undefined;
      if (mode === CommissionProductSelectMode.Type && kindNum) {
        return {
          id: `tax-inc-${index}`,
          productType: 'Accommodation',
          summary: 'Specify product',
          condition: { mode: 'CHOOSE_TYPE', typeId: kindNum, selectedIds: [], typeName: '' },
        };
      }
      if (mode === CommissionProductSelectMode.Select && ids.length) {
        return {
          id: `tax-inc-${index}`,
          productType: 'Accommodation',
          summary: String(ids.length),
          condition: { mode: 'SELECT', selectedIds: ids },
        };
      }
      return {
        id: `tax-inc-${index}`,
        productType: 'Accommodation',
        summary: 'Any',
        condition: { mode: 'ANY', selectedIds: [] },
      };
    }
    case 'addOn': {
      const ids = parsePrefixedIds((raw.addOnIds as unknown[]) ?? [], 'AddOn');
      const kindNum = raw.kind != null && Number(raw.kind) > 0 ? Number(raw.kind) : undefined;
      if (mode === CommissionProductSelectMode.Type && kindNum) {
        return {
          id: `tax-inc-${index}`,
          productType: 'AddOn',
          summary: 'Specify product',
          condition: { mode: 'CHOOSE_TYPE', typeId: kindNum, selectedIds: [], typeName: '' },
        };
      }
      if (mode === CommissionProductSelectMode.Select && ids.length) {
        return {
          id: `tax-inc-${index}`,
          productType: 'AddOn',
          summary: String(ids.length),
          condition: { mode: 'SELECT', selectedIds: ids },
        };
      }
      return {
        id: `tax-inc-${index}`,
        productType: 'AddOn',
        summary: 'Any',
        condition: { mode: 'ANY', selectedIds: [] },
      };
    }
    case 'fee': {
      const ids = parsePrefixedIds((raw.feeIds as unknown[]) ?? [], 'Fee');
      if (mode === CommissionProductSelectMode.Any || ids.length === 0) {
        return {
          id: `tax-inc-${index}`,
          productType: 'Fees',
          summary: 'Any',
          condition: { mode: 'ANY', selectedIds: [] },
        };
      }
      return {
        id: `tax-inc-${index}`,
        productType: 'Fees',
        summary: String(ids.length),
        condition: { mode: 'SELECT', selectedIds: ids },
      };
    }
    default:
      return null;
  }
}

function enrichLaneCardSummary(card: LaneCard, names: TaxProductNameLookup): LaneCard {
  const cond = card.condition;
  if (!cond) return card;
  if (cond.mode === 'SELECT' && 'selectedIds' in cond && cond.selectedIds?.length) {
    let labelNames: string[] = [];
    switch (card.productType) {
      case 'Course':
        labelNames = namesForIds(cond.selectedIds, names.courses);
        break;
      case 'Accommodation':
        labelNames = namesForIds(cond.selectedIds, names.accommodations);
        break;
      case 'AddOn':
        labelNames = namesForIds(cond.selectedIds, names.addons);
        break;
      case 'Transfer':
        labelNames = namesForIds(cond.selectedIds, names.transfers);
        break;
      case 'Fees':
        labelNames = namesForIds(cond.selectedIds, names.fees);
        break;
      default:
        break;
    }
    if (labelNames.length) {
      return { ...card, summary: labelNames.join(', ') };
    }
  }
  if (cond.mode === 'CHOOSE_TYPE' && 'typeId' in cond && cond.typeId) {
    const typeMap = card.productType === 'Accommodation' ? names.accommodationTypes : names.addOnTypes;
    const tn = typeMap.get(cond.typeId);
    if (tn) {
      return { ...card, summary: tn, condition: { ...cond, typeName: tn } };
    }
  }
  return card;
}

export function taxApiBandToRule(dto: TaxBandApiDto, names: TaxProductNameLookup): TaxBandRule {
  const includeRaw = Array.isArray(dto.include) ? dto.include : [];
  const excludeRaw = Array.isArray(dto.exclude) ? dto.exclude : [];
  const include: LaneCard[] = [];
  const exclude: LaneCard[] = [];
  includeRaw.forEach((item, i) => {
    const c = taxApiItemToLaneCard(item as TaxFieldItem, i);
    if (c) include.push(enrichLaneCardSummary(c, names));
  });
  excludeRaw.forEach((item, i) => {
    const c = taxApiItemToLaneCard(item as TaxFieldItem, 1000 + i);
    if (c) exclude.push(enrichLaneCardSummary(c, names));
  });
  const id =
    dto.id != null && Number(dto.id) > 0
      ? String(dto.id)
      : `band-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return {
    id,
    name: dto.name ?? '',
    taxCode: dto.taxCode ?? '',
    taxRatePercent: dto.taxRatePercent ?? 0,
    active: dto.isActive !== false,
    include,
    exclude,
  };
}